import { useState, Dispatch, useEffect } from "react";
import {
  Modal,
  Button,
  Upload,
  Table,
  Alert,
  Popconfirm,
  message,
  Tag,
  Tooltip,
  Spin,
  Progress,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd/lib/upload";

import sdk from "@/sdk";
import {
  BulkAddOrderResult,
  BulkAddOrderRowResult,
  BulkAddOrderRowStatus,
  BulkAddOrderBatch,
  BulkAddOrderBatchRow,
  BulkAddOrderBatchRowStatus,
  BulkAddOrderBatchStatus,
} from "@/graphql";

type FileRef = { base64: string; name: string };
type State =
  | { step: "upload" }
  | { step: "preview"; file: FileRef; result: BulkAddOrderResult }
  | { step: "submitting"; file: FileRef; result: BulkAddOrderResult }
  | { step: "tracking"; batchId: string; batch?: BulkAddOrderBatch };

async function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(",")[1] ?? "");
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function previewRowStatusTag(row: BulkAddOrderRowResult) {
  if (row.status === BulkAddOrderRowStatus.Valid) {
    return <Tag color="green">Бэлэн</Tag>;
  }
  if (row.status === BulkAddOrderRowStatus.Processed) {
    return <Tag color="blue">Цэнэглэгдсэн</Tag>;
  }
  return (
    <Tooltip title={row.errorMessage ?? ""}>
      <Tag color="red">Алдаа</Tag>
    </Tooltip>
  );
}

function batchRowStatusTag(row: BulkAddOrderBatchRow) {
  if (row.status === BulkAddOrderBatchRowStatus.Pending) {
    return <Tag>Хүлээж байна</Tag>;
  }
  if (row.status === BulkAddOrderBatchRowStatus.Success) {
    return row.reverted ? (
      <Tag color="orange">Буцаасан</Tag>
    ) : (
      <Tag color="green">Цэнэглэгдсэн</Tag>
    );
  }
  return (
    <Tooltip title={row.errorMessage ?? ""}>
      <Tag color="red">Алдаа</Tag>
    </Tooltip>
  );
}

export const BulkUploadOrderWidget = ({
  show,
  setShow,
  getOrders,
}: {
  show: boolean;
  setShow: Dispatch<boolean>;
  getOrders: () => void;
}) => {
  const [state, setState] = useState<State>({ step: "upload" });
  const [reverting, setReverting] = useState(false);

  const reset = () => setState({ step: "upload" });
  const close = () => {
    reset();
    setShow(false);
  };

  // Poll batch progress while in tracking step
  useEffect(() => {
    if (state.step !== "tracking") return;
    const batchId = state.batchId;
    let cancelled = false;
    const fetchBatch = async () => {
      try {
        const result = await sdk.getBulkAddOrderBatch({ batchId });
        if (cancelled) return;
        setState((prev) => {
          if (prev.step !== "tracking" || prev.batchId !== batchId) return prev;
          return {
            ...prev,
            batch: result.getBulkAddOrderBatch as BulkAddOrderBatch,
          };
        });
        if (result.getBulkAddOrderBatch.pendingCount === 0) {
          getOrders();
        }
      } catch {
        // ignore, will retry
      }
    };
    fetchBatch();
    const interval = setInterval(fetchBatch, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [state, getOrders]);

  const uploadProps: UploadProps = {
    accept: ".xlsx",
    multiple: false,
    showUploadList: false,
    beforeUpload: async (file) => {
      try {
        const base64 = await readFileAsBase64(file as File);
        const result = await sdk.bulkAddOrder({
          input: { fileBase64: base64, filename: file.name, confirm: false },
        });
        setState({
          step: "preview",
          file: { base64, name: file.name },
          result: result.bulkAddOrder as BulkAddOrderResult,
        });
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const msg = (err as any)?.response?.errors?.[0]?.message;
        message.error(msg ?? "Файл шалгахад алдаа гарлаа.", 5);
      }
      return false;
    },
  };

  const handleConfirm = async () => {
    if (state.step !== "preview") return;
    const prev = state;
    setState({ step: "submitting", file: prev.file, result: prev.result });
    try {
      const result = await sdk.bulkAddOrder({
        input: {
          fileBase64: prev.file.base64,
          filename: prev.file.name,
          confirm: true,
        },
      });
      const batchId = result.bulkAddOrder.batchId;
      if (!batchId) {
        throw new Error("Backend batchId буцаасангүй.");
      }
      message.success(
        `${result.bulkAddOrder.validRows} мөрийг queue-руу илгээлээ. Боловсруулалт хийгдэж байна...`,
        4
      );
      setState({ step: "tracking", batchId });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any)?.response?.errors?.[0]?.message;
      message.error(msg ?? "Цэнэглэлт амжилтгүй боллоо.", 5);
      setState(prev);
    }
  };

  const handleRevert = async () => {
    if (state.step !== "tracking" || !state.batch) return;
    setReverting(true);
    try {
      const result = await sdk.revertBulkAddOrderBatch({
        batchId: state.batchId,
      });
      message.success(
        `${result.revertBulkAddOrderBatch.revertedCount} мөр буцаагдлаа.`,
        3
      );
      // Refetch full batch
      const fresh = await sdk.getBulkAddOrderBatch({ batchId: state.batchId });
      setState({
        step: "tracking",
        batchId: state.batchId,
        batch: fresh.getBulkAddOrderBatch as BulkAddOrderBatch,
      });
      getOrders();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any)?.response?.errors?.[0]?.message;
      message.error(msg ?? "Буцаахад алдаа гарлаа.", 5);
    } finally {
      setReverting(false);
    }
  };

  const previewColumns = [
    { title: "№", dataIndex: "row", key: "row", width: 60 },
    { title: "Имэйл", dataIndex: "email", key: "email" },
    {
      title: "Хэрэглэгч",
      key: "user",
      render: (_: unknown, r: BulkAddOrderRowResult) =>
        r.user ? `${r.user.lastName ?? ""} ${r.user.firstName ?? ""}` : "-",
    },
    {
      title: "Дүн",
      dataIndex: "amount",
      key: "amount",
      render: (v: number | null | undefined) =>
        `${(v ?? 0).toLocaleString()}₮`,
    },
    { title: "Утга", dataIndex: "description", key: "description" },
    {
      title: "Статус",
      key: "status",
      render: (_: unknown, r: BulkAddOrderRowResult) => previewRowStatusTag(r),
    },
  ];

  const batchColumns = [
    { title: "№", dataIndex: "rowNumber", key: "rowNumber", width: 60 },
    { title: "Имэйл", dataIndex: "email", key: "email" },
    {
      title: "Хэрэглэгч",
      key: "user",
      render: (_: unknown, r: BulkAddOrderBatchRow) =>
        r.user ? `${r.user.lastName ?? ""} ${r.user.firstName ?? ""}` : "-",
    },
    {
      title: "Дүн",
      dataIndex: "amount",
      key: "amount",
      render: (v: number) => `${v.toLocaleString()}₮`,
    },
    { title: "Утга", dataIndex: "description", key: "description" },
    {
      title: "Статус",
      key: "status",
      render: (_: unknown, r: BulkAddOrderBatchRow) => batchRowStatusTag(r),
    },
  ];

  const previewResult =
    state.step === "preview" || state.step === "submitting"
      ? state.result
      : null;

  const batch = state.step === "tracking" ? state.batch : null;
  const totalProcessed = batch
    ? batch.totalRows - batch.pendingCount
    : 0;
  const percent = batch
    ? Math.round((totalProcessed / batch.totalRows) * 100)
    : 0;
  const isBatchDone =
    batch != null &&
    batch.status !== BulkAddOrderBatchStatus.Processing;
  const canRevert =
    isBatchDone &&
    batch.successCount > batch.revertedCount &&
    batch.status !== BulkAddOrderBatchStatus.Reverted;

  return (
    <Modal
      width={900}
      title="Бөөнөөр цэнэглэх"
      open={show}
      onCancel={close}
      footer={
        state.step === "upload"
          ? [
              <Button key="cancel" onClick={close}>
                Болих
              </Button>,
            ]
          : state.step === "preview"
            ? [
                <Button key="back" onClick={reset}>
                  ← Буцах
                </Button>,
                <Popconfirm
                  key="confirm"
                  title={`Та ${state.result.validRows} хэрэглэгчийг ${state.result.totalAmount.toLocaleString()}₮-р цэнэглэхдээ итгэлтэй байна уу?`}
                  onConfirm={handleConfirm}
                  disabled={state.result.invalidRows > 0}
                >
                  <Button
                    type="primary"
                    disabled={state.result.invalidRows > 0}
                  >
                    Цэнэглэх ({state.result.validRows} мөр)
                  </Button>
                </Popconfirm>,
              ]
            : state.step === "tracking"
              ? [
                  canRevert ? (
                    <Popconfirm
                      key="revert"
                      title={`${batch?.successCount ?? 0} амжилттай захиалгыг буцаах уу?`}
                      onConfirm={handleRevert}
                    >
                      <Button danger disabled={reverting}>
                        Бүх амжилтыг буцаах
                      </Button>
                    </Popconfirm>
                  ) : null,
                  <Button key="close" type="primary" onClick={close}>
                    Хаах
                  </Button>,
                ]
              : []
      }
    >
      {state.step === "upload" && (
        <>
          <Upload.Dragger {...uploadProps}>
            <p>
              <InboxOutlined style={{ fontSize: 48 }} />
            </p>
            <p>Excel файл сонгох (.xlsx)</p>
          </Upload.Dragger>
          <Alert
            style={{ marginTop: 16 }}
            type="info"
            message="Шаардлага"
            description={
              <>
                <ul style={{ marginBottom: 8 }}>
                  <li>
                    Баганы дараалал: "Харилцагчийн имэйл хаяг", "Мөнгөн дүн",
                    "Гүйлгээний утга"
                  </li>
                  <li>Хамгийн ихдээ 300 мөр</li>
                  <li>Бүх хэрэглэгчийн KYC баталгаажсан, багц сонгосон байх</li>
                </ul>
                <a href="/templates/bulk-deposit-template.xlsx" download>
                  Жишээ файл татах
                </a>
              </>
            }
          />
        </>
      )}
      {previewResult && (
        <>
          <Alert
            style={{ marginBottom: 16 }}
            type={previewResult.invalidRows > 0 ? "warning" : "success"}
            message={`Нийт ${previewResult.totalAmount.toLocaleString()}₮ — ${previewResult.validRows} амжилттай, ${previewResult.invalidRows} алдаатай`}
          />
          <Spin spinning={state.step === "submitting"}>
            <Table
              dataSource={previewResult.rows}
              columns={previewColumns}
              rowKey="row"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Spin>
        </>
      )}
      {state.step === "tracking" && (
        <>
          {!batch && <Spin tip="Дэвшилт авч байна..." />}
          {batch && (
            <>
              <Alert
                style={{ marginBottom: 16 }}
                type={
                  batch.status === BulkAddOrderBatchStatus.Processing
                    ? "info"
                    : batch.failedCount > 0
                      ? "warning"
                      : "success"
                }
                message={
                  batch.status === BulkAddOrderBatchStatus.Processing
                    ? `Боловсруулагдаж байна: ${totalProcessed}/${batch.totalRows}`
                    : `Дууссан — ${batch.successCount} амжилттай, ${batch.failedCount} алдаатай${batch.revertedCount > 0 ? `, ${batch.revertedCount} буцаасан` : ""}`
                }
              />
              <Progress
                percent={percent}
                status={
                  batch.failedCount > 0 && isBatchDone ? "exception" : undefined
                }
                style={{ marginBottom: 16 }}
              />
              {batch.fileUrl && (
                <Alert
                  style={{ marginBottom: 16 }}
                  type="info"
                  message={
                    <a
                      href={batch.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Excel файлыг татах
                    </a>
                  }
                />
              )}
              <Table
                dataSource={batch.rows}
                columns={batchColumns}
                rowKey="rowNumber"
                pagination={{ pageSize: 10 }}
                size="small"
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
};
